import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { meetingStore } from 'store/meetingStore';
import { useJoinMeetingByCode } from 'hooks/useMeeting';
import { MeetingRepository } from 'repository/meetingRepository';
import {
    AppScreen, Button, ButtonFooter, PageIntro, Section, SectionHeader,
} from 'components/ds';
import styles from './JoinWithCode.module.css';

export const JoinWithCode = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const { status, data, error: apiError } = useJoinMeetingByCode();
    const prefix = 'CREW-';
    const [codeSuffix, setCodeSuffix] = useState(''); // 접두사 뒤의 6자리만 관리
    const code = prefix + codeSuffix; // 전체 코드
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasHandledSuccess, setHasHandledSuccess] = useState(false);

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const cursorPos = input.selectionStart ?? input.value.length;
        const oldRaw = input.value;
        const cleaned = oldRaw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);

        // toUpperCase / replace / slice 로 값이 변하면 React 가 DOM value 재설정해 caret 이 끝으로 점프함.
        // 정제 후의 cursor 위치를 "이전 cursor 까지의 유효 문자 수" 로 재계산해 setSelectionRange 로 복원.
        let newPos = cursorPos;
        if (cleaned !== oldRaw) {
            const validBefore = oldRaw.slice(0, cursorPos).toUpperCase().replace(/[^A-Z0-9]/g, '');
            newPos = Math.min(validBefore.length, cleaned.length);
        }

        setCodeSuffix(cleaned);
        setError('');

        // React 가 DOM 갱신 후 caret 복원
        requestAnimationFrame(() => {
            try { input.setSelectionRange(newPos, newPos); } catch { /* 일부 브라우저에서 실패해도 무시 */ }
        });
    };

    const processClipboardText = (text: string) => {
        let cleaned = text.toUpperCase().replace(/[^A-Z0-9-]/g, '');
        // 접두사(CREW-)가 포함되어 있으면 뗀다
        if (cleaned.startsWith(prefix)) {
            cleaned = cleaned.slice(prefix.length);
        }
        // 영문+숫자만 남기고 6자리까지
        cleaned = cleaned.replace(/[^A-Z0-9]/g, '').slice(0, 6);
        if (cleaned) {
            setCodeSuffix(cleaned);
            setError('');
        }
    };

    const validateCode = (code: string): boolean => {
        // Code format: CREW-ABC123
        const codeRegex = /^CREW-[A-Z0-9]{6}$/;
        return codeRegex.test(code);
    };

    // API 상태 변화 감지
    useEffect(() => {
        if (status === 'ready' && data && !hasHandledSuccess) {
            setHasHandledSuccess(true);
            // ApiResponse 래핑 해제: { data: meetingId, message, status } 또는 순수 meetingId 문자열 모두 대응
            const meetingId = typeof data === 'string' ? data : (data?.data || data?.meetingId);
            if (meetingId) {
                // ATTENDING 사전 설정은 fire-and-forget — await 하면 hang 시 "확인중..." 무한 대기 회귀.
                // 모임 상세에서 status 갱신 폴링되므로 background 처리로 충분.
                MeetingRepository.onPutMyMembership(meetingId, { attendanceStatus: 'ATTENDING' })
                    .catch(e => console.warn('코드 참여 후 ATTENDING 사전 설정 실패', e));
                navigate(`/meeting/detail/${meetingId}`, { replace: true });
            } else {
                navigate('/home', { replace: true });
            }
        } else if (status === 'error' && apiError && !hasHandledSuccess) {
            setHasHandledSuccess(true);
            setIsLoading(false);
            const errorMessage = (apiError as any)?.response?.data?.message || (apiError as any)?.message || t('joinCode.joinFail');

            if (errorMessage.includes('이미') || errorMessage.includes('already')) {
                showAlert({
                    title: t('joinCode.failTitle'),
                    message: t('joinCode.alreadyMember'),
                });
            } else if (errorMessage.includes('유효하지') || errorMessage.includes('invalid')) {
                setError(t('joinCode.invalidCode'));
            } else if (errorMessage.includes('최대') || errorMessage.includes('full')) {
                showAlert({
                    title: t('joinCode.failTitle'),
                    message: t('joinCode.meetingFull'),
                });
            } else {
                setError(errorMessage);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, data, apiError, hasHandledSuccess]);

    const handleJoin = () => {
        if (!code.trim()) {
            setError(t('joinCode.enterCode'));
            return;
        }

        if (!validateCode(code)) {
            setError(t('joinCode.wrongFormat'));
            return;
        }

        setIsLoading(true);
        setError('');
        setHasHandledSuccess(false);
        meetingStore.onJoinMeetingByCode(code);
    };

    const isJoinEnabled = code.trim().length > 0 && !isLoading;

    return (
        <AppScreen title={t('joinCode.headerTitle')} bar="sub" hasFooter>
            <PageIntro
                title={t('joinCode.title')}
                desc={<span dangerouslySetInnerHTML={{ __html: t('joinCode.subtitle') }} />}
            />

            <Section spacing="tight">
                <label className={styles.label} htmlFor="invite-code">
                    {t('joinCode.headerTitle')}
                </label>
                <div className={error ? `${styles.codeBox} ${styles.codeBoxError}` : styles.codeBox}>
                    <span className={styles.prefix}>{prefix}</span>
                    <input
                        id="invite-code"
                        type="text"
                        className={styles.code}
                        placeholder="ABC123"
                        value={codeSuffix}
                        onChange={handleCodeChange}
                        onPaste={(e) => {
                            const text = e.clipboardData?.getData('text');
                            if (text) {
                                e.preventDefault();
                                processClipboardText(text);
                            }
                        }}
                        maxLength={6}
                        autoCapitalize="characters"
                    />
                </div>
                {error
                    ? <p className={styles.error}>{error}</p>
                    : <p className={styles.hint}>{t('joinCode.formatHint')}</p>}
            </Section>

            <Section divided last>
                <SectionHeader title={t('joinCode.exampleTitle')} />
                <ul className={styles.examples}>
                    <li>{t('joinCode.example1')}</li>
                    <li>{t('joinCode.example2')}</li>
                    <li>{t('joinCode.example3')}</li>
                </ul>
            </Section>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    disabled={!isJoinEnabled}
                    onClick={handleJoin}
                >
                    {isLoading ? t('joinCode.verifying') : t('joinCode.joinBtn')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
